import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ArrowRight, Sparkles, Zap, Crown } from "lucide-react";
import useScrollToHash from "@/hooks/use-scroll-to-hash";
import ComparisonMatrix from "@/components/app/ComparisonMatrix";

const PLAN_ICONS = { free: Zap, starter: Sparkles, pro: Sparkles, agency: Crown };

export default function Pricing() {
  useScrollToHash();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month"); // month | year
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/plans")
      .then(({ data }) => setPlans(data.plans))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load plans"));
  }, []);

  const checkout = async (lookup) => {
    if (!lookup) return;
    if (!user) {
      navigate("/register?next=/pricing");
      return;
    }
    setBusy(lookup);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
    }
    setBusy("");
  };

  const currentPlan = user?.plan || "free";

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 text-center">
          <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// pricing</p>
          <h1 className="font-semibold tracking-tight mt-3 text-4xl md:text-6xl">
            One workspace.<br />
            <span className="text-primary">Honest pricing.</span>
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-base text-muted-foreground">
            Start with a <strong className="text-foreground">free 7-day Pro trial</strong> — no card required.
            Pay monthly or save ~20% with yearly. Cancel anytime.
          </p>

          <div className="mt-8 inline-flex border border-border bg-white">
            <button
              onClick={() => setInterval("month")}
              className={`px-5 py-2 text-xs font-mono-data uppercase tracking-widest ${
                interval === "month" ? "bg-foreground text-background" : ""
              }`}
              data-testid="billing-interval-month">
              Monthly
            </button>
            <button
              onClick={() => setInterval("year")}
              className={`px-5 py-2 text-xs font-mono-data uppercase tracking-widest border-l border-border ${
                interval === "year" ? "bg-foreground text-background" : ""
              }`}
              data-testid="billing-interval-year">
              Yearly · save 20%
            </button>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Sparkles;
            const isCurrent = currentPlan === p.id;
            const priceCents = interval === "year" ? p.price_yearly_cents : p.price_monthly_cents;
            const priceLabel = p.id === "free"
              ? "$0"
              : `$${Math.floor(priceCents / 100)}`;
            const periodLabel = p.id === "free" ? "/ forever" : (interval === "year" ? "/ year" : "/ month");
            const lookup = interval === "year" ? p.lookup_yearly : p.lookup_monthly;
            const primary = !!p.popular;
            return (
              <div key={p.id}
                className={`border p-8 tilt-hover relative ${
                  primary ? "border-foreground bg-foreground text-background" : "border-border bg-white"
                }`}
                data-testid={`pricing-card-${p.id}`}
              >
                {primary && (
                  <span className="absolute -top-3 left-6 bg-primary text-background text-[10px] font-mono-data uppercase tracking-widest px-2 py-0.5">
                    most popular
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${primary ? "" : ""}`} strokeWidth={1.5} />
                    <h3 className="font-semibold text-2xl tracking-tight">{p.name}</h3>
                  </div>
                  {isCurrent && (
                    <span className={`text-[10px] font-mono-data uppercase tracking-widest border px-1.5 py-0.5 ${
                      primary ? "border-background/40" : "border-foreground/40"
                    }`}>
                      current
                    </span>
                  )}
                </div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-semibold text-5xl tracking-tight">{priceLabel}</span>
                  <span className={`text-sm ${primary ? "text-background/60" : "text-muted-foreground"}`}>{periodLabel}</span>
                </div>
                {interval === "year" && p.id !== "free" && (
                  <p className={`font-mono-data text-xs uppercase tracking-widest mt-1 ${primary ? "text-background/60" : "text-muted-foreground"}`}>
                    ≈ ${Math.round(p.price_yearly_cents / 12 / 100)}/mo · saves ${Math.round((p.price_monthly_cents*12 - p.price_yearly_cents) / 100)}
                  </p>
                )}
                <ul className="mt-6 space-y-2 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  {p.id === "free" ? (
                    !user ? (
                      <Link to="/register">
                        <Button className="w-full rounded-none h-11 bg-foreground text-background hover:bg-foreground/90" data-testid={`pricing-cta-${p.id}`}>
                          Start free <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    ) : (
                      <Button disabled className="w-full rounded-none h-11" data-testid={`pricing-cta-${p.id}`}>
                        Current plan
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={() => checkout(lookup)}
                      disabled={busy === lookup || isCurrent}
                      className={`w-full rounded-none h-11 ${
                        primary
                          ? "bg-background text-foreground hover:bg-background/90"
                          : "bg-foreground text-background hover:bg-foreground/90"
                      }`}
                      data-testid={`pricing-cta-${p.id}`}
                    >
                      {isCurrent ? "Current plan" : (busy === lookup ? "Redirecting…" : p.cta)}
                      {!isCurrent && busy !== lookup && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* COMPARISON MATRIX */}
      <section id="compare" className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <div className="mb-8">
            <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// compare</p>
            <h2 className="font-semibold tracking-tight text-3xl md:text-4xl mt-2">What you get at each tier.</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              A feature-by-feature look so you can pick the plan that matches how you actually work.
            </p>
          </div>
          <ComparisonMatrix />
        </div>
      </section>

      {/* FAQ-ish */}
      <section id="faq" className="border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
          <h2 className="font-semibold tracking-tight text-3xl">Common questions.</h2>
          <div className="grid md:grid-cols-2 gap-8 mt-8 text-sm">
            <div>
              <h4 className="font-semibold tracking-tight text-base">Can I cancel anytime?</h4>
              <p className="text-muted-foreground mt-1">Yes — cancel from the Customer Portal. Access continues until the end of the billing period; no refunds for partial months.</p>
            </div>
            <div>
              <h4 className="font-semibold tracking-tight text-base">What&apos;s the difference between Starter and Pro?</h4>
              <p className="text-muted-foreground mt-1">Starter ($19/mo) is for single-site small businesses — get the Simple Mode dashboard, AI inline fixes and WordPress publish. Pro ($39/mo) unlocks 5 projects, Content Gap analysis and the full Content Studio for agencies and multi-site owners.</p>
            </div>
            <div>
              <h4 className="font-semibold tracking-tight text-base">Do you handle tax / VAT?</h4>
              <p className="text-muted-foreground mt-1">Yes. Stripe calculates, collects, and remits the right tax in your country automatically — you&apos;ll see it on your receipt.</p>
            </div>
            <div>
              <h4 className="font-semibold tracking-tight text-base">Is this test mode?</h4>
              <p className="text-muted-foreground mt-1">Yes — until live keys are configured. Use card <span className="font-mono-data">4242 4242 4242 4242</span>, any future expiry, any CVC.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex items-center justify-between">
          <span className="font-semibold tracking-tight">ARLOSEO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to site</Link>
        </div>
      </footer>
    </div>
  );
}
